<template>
  <div>
    <b-form-group :label="trans('account_owner')" class="mb-4">
      <b-form-radio-group
        v-model="assign"
        :options="assignOptions"
        stacked
        :disabled="busy"
      ></b-form-radio-group>
    </b-form-group>

    <form-person-question
      v-if="assign == 'new'"
      :organization="organization"
      :question="question"
      hide-label
      v-model="account_owner"
      :disabled="busy"
    ></form-person-question>

    <b-row v-if="assign == 'existing'">
      <b-col v-if="!!account_owner">
        <address class="border ml-4 p-2">
          <strong class="b-block">{{ account_owner.name }}</strong>
          <span class="d-block">{{ account_owner.email }}</span>
          <span v-if="!!account_owner.mobile" class="d-block">{{ account_owner.mobile }}</span>
        </address>
      </b-col>
      <b-col>
        <b-button variant="outline-primary" @click="onClickChooseClient" :disabled="busy">
          {{ trans('choose_a_client') }}
        </b-button>
      </b-col>
    </b-row>
    <br/>

    <div v-if="!!account_owner" class="mb-4">
      <b-row>
        <b-col>
          <b-form-group>
            <label>
              {{ trans('item') }}
              <small class="text-muted" :title="trans('add_booking_name_about')" v-b-tooltip.hover>
                <i class="fa fa-fw fa-info-circle"></i>
              </small>
            </label>
            <b-form-input v-model="name" size="sm" :placeholder="booking.name" :disabled="busy" />
          </b-form-group>
        </b-col>
        <b-col>
          <b-row>
            <b-col>
              <b-form-group :label="trans('price')">
                <b-form-input
                  v-model="price_per_duration"
                  size="sm"
                  type="number"
                  step="0.01"
                  min="0"
                  :state="pricePerDurationState"
                  :disabled="busy"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group :label="trans('per_duration')">              
                <b-form-select
                  v-model="per_60_minute_duration"
                  size="sm"
                  :options="durationOptions"
                  :placeholder="trans('duration')"
                  :disabled="busy"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group :label="trans('price')">
            <b-form-input v-model="price" type="number" step="0.01" min="0" size="sm" :state="priceState" :disabled="busy" />
            <b-form-text v-if="priceState !== false && pricePerDurationState !== false && duration > 0 && calculatedPrice == price">
              <em>{{ currency(price_per_duration) }} x {{ durationHours }}h = {{ currency(price) }}</em>
            </b-form-text>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group :label="trans('taxes')">
            <model-treeselect
              :organization="organization"
              :url="!all_taxes ? `/organization/${organization.id}/tax/list` : undefined"
              :models="all_taxes"
              multiple
              v-model="tax_ids"
              :placeholder="trans('taxes')"
              :disabled="busy"
              @loaded="all_taxes = $event; $emit('all-taxes', $event)"
            ></model-treeselect>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group :label="trans('gl')">
            <model-treeselect
              :organization="organization"
              :url="!all_gls ? `/organization/${organization.id}/gl/list?all=true` : undefined"
              :models="all_gls"
              :multiple="false"
              v-model="gl_id"
              :placeholder="trans('code')"
              :disabled="busy"
              @loaded="all_gls = $event; $emit('all-gls', $event)"
            ></model-treeselect>
            <b-form-text v-if="glIsMissing">
              <span class="text-danger">{{ trans('required') }}</span>
            </b-form-text>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <admin-choose-client-modal
      :id="adminChooseClientModalId"
      :organization="organization"
      @chosen="onChooseClient"
    ></admin-choose-client-modal>
  </div>
</template>

<script>
  import AdminChooseClientModal from './../client/AdminChooseClientModal'
  import FormPersonQuestion from './../form-questions/FormPersonQuestion'
  import ModelTreeselect from './../../../common/components/ModelTreeselect'

  function isEmpty(value) {
    return ((value||'')+'').trim().length == 0
  }

  let blankPerson = {
    slug: 'account_owner',
    type: 'account_owner',
    label: trans('account_owner'),
    description: trans('checkout_form_email_about'),
    required: true,
    options: {
      name: 'required',
      email: 'required',
      mobile: 'optional',
      gender: 'optional',
      gender_choices: null,
      date_of_birth: 'optional',
      age_constraint: null,
      grade: null,
      friend: null,
      pickup_password: null,
      other_phone: 'optional',
      other_phone_label: null,
      address: 'optional',
      zip_postal_code: null,
      include_on_invoice: false,
    },
  }

  export default {
    props: {
      organization: Object,
      booking: Object,
      pkg: Object,
      allTaxes: Array,
      allGls: Array,
      value: Object,
    },
    components: {
      AdminChooseClientModal,
      FormPersonQuestion,
      ModelTreeselect,
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        numeral: numeral,
        currency: _.currency,
        isEmpty: isEmpty,
        busy: false,
        error: null,
        account_owner: null,
        question: _.cloneDeep(blankPerson),
        assign: 'existing',
        assignOptions: [
          {value: 'existing', text: trans('assign_existing_client')},
          {value: 'new', text: trans('assign_new_client')},
        ],
        name: null,
        price: 10,
        tax_ids: [],
        gl_id: null,
        all_taxes: this.allTaxes,
        all_gls: this.allGls,
        price_per_duration: 10,
        per_60_minute_duration: 60,
      }
    },
    computed: {
      pricePerDurationNumber() {
        if (isNaN(this.price_per_duration)) return null
        return _.round(this.price_per_duration, 2)
      },
      pricePerDurationState() {
        if (this.pricePerDurationNumber === null) return false
        return null
      },
      durationOptions() {
        let options = []
        for (let i = 5; i <= 1080; i += 5) {
          let h = Math.floor(i/60)
          let m = i % 60
          options.push({
            value: i,
            text: (h > 0 ? `${h}h` : '') + (h > 0 && m > 0 ?' ' : '') + (m > 0 ? `${m}m` : '')
          })
        }
        return options
      },
      duration() {
        if (!this.booking) return 0
        let taken_periods = []
        return _.reduce(this.booking.events, (sum, e) => {
          let period = e.start + '_' + e.duration
          if (taken_periods.indexOf(period) === -1) {
            sum += e.duration
            taken_periods.push(period)
          }
          return sum
        }, 0)
      },
      durationHours() {
        if (this.duration == 0) return 0
        return _.round(this.duration / 60, 2)
      },
      calculatedPrice() {
        if (this.price_per_duration > 0 && this.per_60_minute_duration > 0 && this.duration > 0) {
          return _.round(this.price_per_duration * this.duration/this.per_60_minute_duration, 2)
        }
        return this.price_per_duration
      },
      adminChooseClientModalId() {
        return `build-invoice-info-admin-choose-client-modal-${!!this.booking && !!this.booking.id ? this.booking.id : ''}`
      },
      glIsMissing() {
        return !this.gl_id && this.organization.gls_are_required
      },
      priceState() {
        if (isNaN(this.price) || parseFloat(this.price) < 0) return false
        return null
      },
      canCreate() {
        return !!this.account_owner &&
          !isEmpty(this.account_owner.first_name) &&
          !isEmpty(this.account_owner.last_name) &&
          !isEmpty(this.account_owner.email) &&
          (isEmpty(this.account_owner.mobile) || _.validatePhone(this.account_owner.mobile)) &&
          this.priceState !== false &&
          !this.glIsMissing
      },
      serialized() {
        return {
          account_owner: this.account_owner,
          name: this.name,
          price: this.price,
          tax_ids: this.tax_ids,
          gl_id: this.gl_id,
          price_per_duration: this.price_per_duration,
          per_60_minute_duration: this.per_60_minute_duration,
          duration: this.duration,
        }
      },
    },
    watch: {
      serialized() {
        let serialized = this.canCreate ? this.serialized : null
        if (!_.isEqual(this.value, serialized)) {
          this.$emit('input', serialized)
        }
      },
      pkg() {
        if (!this.pkg) return
        this.tax_ids = this.pkg.tax_ids
        this.gl_id = this.pkg.gl_id
        this.price_per_duration = this.pkg.price
        if (!!this.pkg.rules[0]) {
          this.per_60_minute_duration = this.pkg.rules[0].duration
        }
        this.updatePrice()
      },
      duration() {
        this.updatePrice()
      },
      price_per_duration() {
        this.updatePrice()
      },
      per_60_minute_duration() {
        this.updatePrice()
      },
    },
    methods: {
      updatePrice() {
        this.$nextTick(() => {
          //console.log('updatePrice', this.price, this.calculatedPrice)
          this.price = this.calculatedPrice
        })
      },
      deserialize() {
        if (!!this.value) {
          this.account_owner = !!this.value.account_owner ? _.clone(this.value.account_owner) : null
          this.name = this.value.name
          this.price = this.value.price
          this.tax_ids = this.value.tax_ids
          this.gl_id = this.value.gl_id
          this.price_per_duration = this.value.price_per_duration || 10
          this.per_60_minute_duration = this.value.per_60_minute_duration || 60
        } else {
          this.account_owner = null
          this.name = null
          this.tax_ids = !!this.pkg ? this.pkg.tax_ids : []
          this.gl_id = !!this.pkg ? this.pkg.gl_id : null
          this.price_per_duration = !!this.pkg ? this.pkg.price : 10
          this.per_60_minute_duration = !!this.pkg && !!this.pkg.rules[0] ? this.pkg.rules[0].duration : 60
          this.updatePrice()
        }
      },
      onClickChooseClient() {
        this.$bvModal.show(this.adminChooseClientModalId)
      },
      onChooseClient(client) {
        if (!client) {
          this.account_owner = null
        } else {
          this.account_owner = {
            first_name: client.first_name,
            last_name: client.last_name,
            name: _.compact([client.first_name, client.last_name]).join(' '),
            email: client.email,
            mobile: client.mobile,
          }
        }
      },
    },
    mounted() {
      window[`buildInvoiceForBooking${!!this.booking && !!this.booking.id ? this.booking.id : ''}`] = this
      this.deserialize()
    },
  }
</script>